import { useEffect, useState } from "react";
import { Alert, Box, Button, LinearProgress, Paper, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Replay as RetryIcon } from "@mui/icons-material";
import type { PlaybookSummary } from "../../services/lensService";
import { formatElapsed } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginBottom: theme.spacing(4) },
    meta: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(4),
        marginTop: theme.spacing(2),
        color: "#777",
    },
    elapsed: { fontVariantNumeric: "tabular-nums" },
    failed: { marginBottom: theme.spacing(4) },
}));

interface PlaybookGenerationStatusProps {
    playbook: PlaybookSummary;
    retrying: boolean;
    onRetry: () => void;
}

// The playbook's own record of its AI generation, so it shows the same wherever the generation was started from.
const PlaybookGenerationStatus = ({ playbook, retrying, onRetry }: PlaybookGenerationStatusProps) => {
    const { classes } = useStyles();
    const [now, setNow] = useState(() => Date.now());
    const generating = playbook.generationStatus === "generating";

    useEffect(() => {
        if (!generating) return;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [generating]);

    if (playbook.generationStatus === "failed") {
        return (
            <Alert
                severity="error"
                className={classes.failed}
                action={
                    !playbook.archived && (
                        <Button color="inherit" size="small" startIcon={<RetryIcon />} onClick={onRetry} disabled={retrying}>
                            Retry
                        </Button>
                    )
                }
            >
                {playbook.generationError ?? "Generation stopped before it finished."}
            </Alert>
        );
    }

    if (!generating) return null;

    const startedAt = playbook.generationStartedOn ? new Date(playbook.generationStartedOn).getTime() : NaN;

    return (
        <Paper className={classes.root} role="status">
            <Typography variant="body2" gutterBottom>
                {playbook.generationProgress ?? "Drafting with AI"}
            </Typography>
            <LinearProgress />
            <Box className={classes.meta}>
                <Typography variant="caption">This can take up to about 15 minutes; you can leave this page.</Typography>
                {!isNaN(startedAt) && (
                    <Typography variant="caption" className={classes.elapsed}>
                        {formatElapsed(now - startedAt)}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default PlaybookGenerationStatus;
